/**
 * KRA Photo Journal – usePhotos Hook
 *
 * Manages the full photo list state: pagination, filtering, sorting.
 * Implements cursor-based pagination using Airtable's offset tokens.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import type { PhotoRecord } from "@kra/photo-journal-shared";
import { fetchPhotos } from "../lib/api.js";

export type SortField = "returnDate" | "shootDate";
export type SortDir   = "desc" | "asc";

export interface UsePhotosOptions {
  photographer?: string;
  sortField: SortField;
  sortDir: SortDir;
  search?: string;
  /** When set, fetch only these record IDs (used for collection filtering). */
  recordIds?: string[];
  /** Drive category folder ("06. Furisode cao cap 2"). Filtered server-side —
   *  the feed is paginated, so a client-side filter would only see one page. */
  folder?: string;
  /** Quick-filter chip. Server-side for the same reason as `folder`: the first
   *  liked set can sit hundreds of records deep, and a client-side filter over
   *  the loaded page would render an empty grid — which also unmounts the
   *  infinite-scroll sentinel, so it could never page far enough to find one. */
  quick?: "liked" | "downloaded" | "not-downloaded";
  /** With `quick: "liked"`, restrict to sets this photographer liked.
   *  Omit for the admin view = liked by anyone. */
  likedBy?: string;
}

export interface UsePhotosResult {
  photos: PhotoRecord[];
  isLoading: boolean;
  isLoadingMore: boolean;
  hasMore: boolean;
  error: string | null;
  loadMore: () => void;
  reset: () => void;
  /** Optimistically patch a single record in the local photos array. */
  updatePhoto: (id: string, patch: Partial<PhotoRecord>) => void;
}

const PAGE_SIZE = 48;

export function usePhotos(options: UsePhotosOptions): UsePhotosResult {
  const [photos, setPhotos] = useState<PhotoRecord[]>([]);
  const [offset, setOffset] = useState<string | null>(null);
  const [hasMore, setHasMore] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Track the current "session" to discard stale responses after filter/sort change
  const sessionRef = useRef(0);

  const loadInitial = useCallback(async (session: number) => {
    setIsLoading(true);
    setError(null);

    try {
      const data = await fetchPhotos({
        pageSize: PAGE_SIZE,
        photographer: options.photographer || undefined,
        sort: options.sortField,
        sortDir: options.sortDir,
        search: options.search || undefined,
        recordIds: options.recordIds,
        folder: options.folder || undefined,
        quick: options.quick,
        likedBy: options.likedBy || undefined,
      });

      if (session !== sessionRef.current) return; // stale response

      setPhotos(data.records);
      setOffset(data.offset);
      setHasMore(data.offset !== null);
    } catch (err) {
      if (session !== sessionRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load photos");
    } finally {
      if (session === sessionRef.current) setIsLoading(false);
    }
  }, [options.photographer, options.sortField, options.sortDir, options.search, options.recordIds, options.folder, options.quick, options.likedBy]);

  // Reset and reload when filter/sort changes
  useEffect(() => {
    sessionRef.current++;
    const session = sessionRef.current;
    setPhotos([]);
    setOffset(null);
    setHasMore(true);
    loadInitial(session);
  }, [loadInitial]);

  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore || !offset) return;

    setIsLoadingMore(true);
    const session = sessionRef.current;

    try {
      const data = await fetchPhotos({
        offset,
        pageSize: PAGE_SIZE,
        photographer: options.photographer || undefined,
        sort: options.sortField,
        sortDir: options.sortDir,
        search: options.search || undefined,
        recordIds: options.recordIds,
        folder: options.folder || undefined,
        quick: options.quick,
        likedBy: options.likedBy || undefined,
      });

      if (session !== sessionRef.current) return;

      setPhotos((prev) => [...prev, ...data.records]);
      setOffset(data.offset);
      setHasMore(data.offset !== null);
    } catch (err) {
      if (session !== sessionRef.current) return;
      setError(err instanceof Error ? err.message : "Failed to load more");
    } finally {
      if (session === sessionRef.current) setIsLoadingMore(false);
    }
  }, [isLoadingMore, hasMore, offset, options.photographer, options.sortField, options.sortDir, options.search, options.recordIds, options.folder, options.quick, options.likedBy]);

  const reset = useCallback(() => {
    sessionRef.current++;
    setPhotos([]);
    setOffset(null);
    setHasMore(true);
    setError(null);
    loadInitial(sessionRef.current);
  }, [loadInitial]);

  const updatePhoto = useCallback((id: string, patch: Partial<PhotoRecord>) => {
    setPhotos((prev) => prev.map((p) => (p.id === id ? { ...p, ...patch } : p)));
  }, []);

  return { photos, isLoading, isLoadingMore, hasMore, error, loadMore, reset, updatePhoto };
}
